import { useState } from 'react';
import { 
  Fingerprint, ScanFace, Shield, Lock, Unlock, AlertCircle, Check, 
  Loader2, Clock, Eye, Timer
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { useAppLock, LOCK_TIMEOUT_OPTIONS } from '@/contexts/AppLockContext';
import { useAuth } from '@/contexts/AuthContext';
import useBiometricAuth from '@/hooks/useBiometricAuth';
import { cn } from '@/lib/utils';

/**
 * AppLockSettings - Security settings for app lock
 * 
 * Allows users to:
 * - Enable/disable biometric app lock
 * - Configure lock timeout duration
 * - Configure lock behavior (on background, for sensitive actions)
 * - Enroll/remove biometric credentials
 */
const AppLockSettings = () => {
  const { user } = useAuth();
  const { 
    appLockEnabled, 
    lockOnBackground,
    lockTimeout,
    requireForSensitive,
    enableAppLock, 
    disableAppLock,
    toggleLockOnBackground,
    setLockTimeoutDuration,
    toggleRequireForSensitive
  } = useAppLock();
  
  const {
    isSupported,
    isEnrolled,
    isAuthenticating,
    enrollBiometric,
    unenroll,
    authenticate,
  } = useBiometricAuth();

  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showTimeoutOptions, setShowTimeoutOptions] = useState(false);

  // Get platform-specific biometric name
  const getBiometricName = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) return 'Face ID / Touch ID';
    return 'Fingerprint / PIN';
  };

  const getBiometricIcon = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) return <ScanFace className="h-5 w-5" />;
    return <Fingerprint className="h-5 w-5" />;
  };

  const getTimeoutLabel = () => {
    const option = LOCK_TIMEOUT_OPTIONS.find(o => o.value === lockTimeout);
    return option?.label || '30 seconds';
  };

  const handleEnableAppLock = async () => {
    if (!isSupported) {
      toast.error('Biometric authentication is not available on this device');
      return;
    }

    setEnrolling(true);
    
    try {
      // First, verify with existing biometric/passcode
      const verified = await authenticate();
      
      if (verified) {
        // Then enroll the credential
        await enrollBiometric(user?.email || user?.id || 'user');
        enableAppLock();
        toast.success('App Lock enabled', {
          description: `Your app is now protected with ${getBiometricName()}`,
          icon: <Shield className="h-4 w-4 text-emerald-500" />
        });
      }
    } catch (err) {
      console.error('Failed to enable app lock:', err);
      toast.error('Failed to enable App Lock', {
        description: err.message || 'Please try again'
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleDisableAppLock = async () => {
    setVerifying(true);
    
    try {
      // Verify identity before disabling
      if (isSupported && isEnrolled) {
        const verified = await authenticate();
        if (!verified) {
          toast.error('Verification failed');
          return;
        }
      }
      
      disableAppLock();
      unenroll();
      toast.success('App Lock disabled', {
        description: 'Your app is no longer protected by biometrics'
      });
    } catch (err) {
      console.error('Failed to disable app lock:', err);
      toast.error('Failed to disable App Lock', {
        description: err.message || 'Please try again'
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleToggleLockOnBackground = (enabled) => {
    toggleLockOnBackground(enabled);
    toast.success(enabled ? 'Lock on background enabled' : 'Lock on background disabled', {
      description: enabled 
        ? `App will lock after ${getTimeoutLabel().toLowerCase()} in background` 
        : 'App will stay unlocked when switching apps'
    });
  };

  const handleTimeoutChange = (value) => {
    const timeoutMs = parseInt(value, 10);
    setLockTimeoutDuration(timeoutMs);
    const option = LOCK_TIMEOUT_OPTIONS.find(o => o.value === timeoutMs);
    toast.success('Lock timeout updated', {
      description: option?.description || `Lock after ${option?.label}`
    });
    setShowTimeoutOptions(false);
  };

  const handleToggleRequireForSensitive = (enabled) => {
    toggleRequireForSensitive(enabled);
    toast.success(enabled ? 'Sensitive action protection enabled' : 'Sensitive action protection disabled', {
      description: enabled 
        ? 'Biometric required to view/share emergency info' 
        : 'Quick access to all features'
    });
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="app-lock-settings">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-5 w-5 text-sky-500" />
          App Lock
        </CardTitle>
        <CardDescription>
          Protect your personal information with biometric authentication
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Biometric support status */}
        {!isSupported && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl text-amber-800 text-sm">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Biometric not available</p>
              <p className="text-amber-600 text-xs mt-0.5">
                Your device doesn't support biometric authentication, or it's not set up.
              </p>
            </div>
          </div>
        )}

        {/* Main App Lock toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center",
              appLockEnabled ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-400"
            )}>
              {appLockEnabled ? <Lock className="h-5 w-5" /> : <Unlock className="h-5 w-5" />}
            </div>
            <div>
              <Label className="text-sm font-medium cursor-pointer">
                Enable App Lock
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Require {getBiometricName()} to open the app
              </p>
            </div>
          </div>
          
          {appLockEnabled ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisableAppLock}
              disabled={verifying}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              {verifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Disable'
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleEnableAppLock}
              disabled={!isSupported || enrolling}
              className="bg-sky-500 hover:bg-sky-600"
            >
              {enrolling ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : getBiometricIcon()}
              <span className="ml-2">Enable</span>
            </Button>
          )}
        </div>

        {/* Additional settings (only show when app lock is enabled) */}
        {appLockEnabled && (
          <div className="space-y-4 pt-4 border-t border-slate-100">
            {/* Lock Timeout Duration */}
            <div className="space-y-3">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowTimeoutOptions(!showTimeoutOptions)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Timer className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium cursor-pointer">
                      Lock Timeout
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      How long before the app locks
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                    {getTimeoutLabel()}
                  </span>
                </div>
              </div>

              {/* Timeout options */}
              {showTimeoutOptions && (
                <div className="ml-13 pl-13 bg-slate-50 rounded-xl p-3">
                  <RadioGroup
                    value={lockTimeout.toString()}
                    onValueChange={handleTimeoutChange}
                    className="space-y-2"
                  >
                    {LOCK_TIMEOUT_OPTIONS.map((option) => (
                      <div 
                        key={option.value}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                          lockTimeout === option.value ? "bg-white shadow-sm" : "hover:bg-white/50"
                        )}
                      >
                        <RadioGroupItem 
                          value={option.value.toString()} 
                          id={`timeout-${option.value}`}
                        />
                        <Label 
                          htmlFor={`timeout-${option.value}`}
                          className="flex-1 cursor-pointer"
                        >
                          <span className="text-sm font-medium">{option.label}</span>
                          <span className="text-xs text-slate-500 block">{option.description}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>

            {/* Lock on background */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-slate-400" />
                </div>
                <div>
                  <Label className="text-sm font-medium cursor-pointer">
                    Lock when backgrounded
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Lock app after switching to another app
                  </p>
                </div>
              </div>
              <Switch
                checked={lockOnBackground}
                onCheckedChange={handleToggleLockOnBackground}
                data-testid="lock-on-background-toggle"
              />
            </div>

            {/* Require unlock for sensitive actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-rose-500" />
                </div>
                <div>
                  <Label className="text-sm font-medium cursor-pointer">
                    Protect sensitive actions
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Require unlock to view/share emergency info
                  </p>
                </div>
              </div>
              <Switch
                checked={requireForSensitive}
                onCheckedChange={handleToggleRequireForSensitive}
                data-testid="require-sensitive-toggle"
              />
            </div>
          </div>
        )}

        {/* Status indicator */}
        {appLockEnabled && (
          <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full">
              <Check className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">
                Protected with {getBiometricName()}
              </span>
            </div>
          </div>
        )}

        {/* Privacy note */}
        <p className="text-xs text-slate-400 pt-2">
          Your biometric data never leaves your device. We only verify that you are the device owner.
        </p>
      </CardContent>
    </Card>
  );
};

export default AppLockSettings;
